import React, { useCallback, useEffect, useState } from "react";
import axios from "../../axiosConfig";
import { formatExcelSourceLocation, isExcelSource } from "./rfpUtils";

const API = "/api";

export default function RfpPdfPanel({ jobId, row, onClose, onCollapse, sourceFormat = "pdf" }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [nearbyText, setNearbyText] = useState("");
  const isDocx = sourceFormat === "docx";
  const isExcel = isExcelSource(sourceFormat);
  const page = row?.pdf_page ?? row?.source_start ?? row?.sequence;
  const excelLabel = formatExcelSourceLocation(row?.source_location);

  const loadPdf = useCallback(async () => {
    if (!jobId || isDocx || isExcel) return;
    try {
      const res = await axios.get(`${API}/rfp-jobs/${jobId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch {
      setPdfUrl(null);
    }
  }, [jobId, isDocx, isExcel]);

  const loadExcerpt = useCallback(async () => {
    if (!jobId || !isDocx) {
      setNearbyText("");
      return;
    }
    try {
      const res = await axios.get(`${API}/rfp-jobs/${jobId}/source-excerpt`, {
        params: { page: page || 1 },
      });
      setNearbyText((res.data?.text || "").trim());
    } catch {
      setNearbyText("");
    }
  }, [jobId, isDocx, page]);

  useEffect(() => {
    loadPdf();
    return () => {
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [loadPdf]);

  useEffect(() => {
    loadExcerpt();
  }, [loadExcerpt]);

  if (!row) return null;

  return (
    <aside className="flex w-full shrink-0 flex-col border-t border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-950/50 lg:w-80 lg:border-l lg:border-t-0">
      <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-700">
        <h4 className="text-sm font-semibold">Source context</h4>
        <div className="flex items-center gap-2">
          {onCollapse && (
            <button
              type="button"
              title="Collapse panel"
              className="hidden text-xs text-slate-500 hover:underline lg:inline"
              onClick={onCollapse}
            >
              Collapse
            </button>
          )}
          <button type="button" className="text-xs text-slate-500 hover:underline lg:hidden" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-3 text-sm">
        <div>
          <span className="text-xs font-medium uppercase text-slate-500">
            {isExcel ? "Worksheet / rows" : isDocx ? "Logical page / row" : "Approx. page / row"}
          </span>
          <p className="mt-0.5 font-mono text-emerald-700 dark:text-emerald-300">
            {isExcel ? excelLabel || "—" : page ?? "—"}
          </p>
        </div>
        <div>
          <span className="text-xs font-medium uppercase text-slate-500">Section</span>
          <p className="mt-0.5">{row.section_title || "Document"}</p>
        </div>
        <div>
          <span className="text-xs font-medium uppercase text-slate-500">Requirement excerpt</span>
          <p className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2 text-xs leading-relaxed dark:border-slate-700 dark:bg-slate-900">
            {row.text || "(empty)"}
          </p>
        </div>
        {isDocx && nearbyText && nearbyText !== (row.text || "").trim() && (
          <div>
            <span className="text-xs font-medium uppercase text-slate-500">Nearby source text</span>
            <p className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap rounded-lg border border-slate-200 bg-white p-2 text-xs leading-relaxed dark:border-slate-700 dark:bg-slate-900">
              {nearbyText}
            </p>
          </div>
        )}
        {pdfUrl && (
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex rounded-lg border border-emerald-600 px-3 py-1.5 text-xs font-medium text-emerald-800 dark:text-emerald-200"
          >
            Open PDF in new tab
          </a>
        )}
        {pdfUrl && (
          <iframe
            title="RFP PDF preview"
            src={`${pdfUrl}#page=${page || 1}`}
            className="hidden h-48 w-full rounded-lg border border-slate-200 dark:border-slate-700 lg:block"
          />
        )}
      </div>
    </aside>
  );
}
